import React from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { Clock, ArrowLeft, ChevronRight, Calendar, User } from "lucide-react";
import Layout from "../components/Layout";
import SEOHead from "../components/SEOHead";
import { getPostBySlug, getRelatedPosts } from "../data/blog";

export default function BlogPost() {
  const { slug } = useParams();
  const post = getPostBySlug(slug);
  if (!post) return <Navigate to="/blog" replace />;

  const isEs = post.lang === "es";
  const related = getRelatedPosts(post, 3);
  const altPost = post.altSlug ? getPostBySlug(post.altSlug) : null;

  const articleJsonLd = {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    headline: post.title,
    description: post.excerpt,
    datePublished: post.publishedAt,
    dateModified: post.publishedAt,
    inLanguage: isEs ? "es-US" : "en-US",
    author: { "@type": "Organization", name: post.author },
    publisher: {
      "@type": "Organization",
      name: "Velcor Energy",
      logo: { "@type": "ImageObject", url: "https://velcorenergy.com/logo.png" },
    },
    image: post.cover,
    mainEntityOfPage: { "@type": "WebPage", "@id": `/blog/${post.slug}` },
  };

  const alternates = altPost
    ? [
        { hreflang: isEs ? "en-us" : "es-us", href: `/blog/${altPost.slug}` },
        { hreflang: "x-default", href: `/blog/${post.altSlug || post.slug}` },
      ]
    : [];

  const formatDate = (iso) => {
    try {
      return new Date(iso).toLocaleDateString(isEs ? "es-US" : "en-US", {
        year: "numeric",
        month: "long",
        day: "numeric",
      });
    } catch {
      return iso;
    }
  };

  return (
    <Layout>
      <SEOHead
        title={post.title}
        description={post.excerpt}
        canonical={`/blog/${post.slug}`}
        ogImage={post.cover}
        alternates={alternates}
        jsonLd={articleJsonLd}
      />

      {/* Back link */}
      <div className="max-w-[760px] mx-auto px-5 pt-10">
        <Link
          to="/blog"
          data-testid="blog-back-link"
          className="inline-flex items-center gap-1.5 text-slate-500 hover:text-velcor-green text-[13px] font-bold transition-colors"
        >
          <ArrowLeft className="w-4 h-4" /> {isEs ? "Volver al blog" : "Back to blog"}
        </Link>
      </div>

      {/* Header */}
      <article className="max-w-[760px] mx-auto px-5 pt-8 pb-16" data-testid="blog-post-article">
        <header className="mb-10">
          <div className="flex items-center gap-3 text-[11px] font-extrabold uppercase tracking-[0.16em] mb-5">
            <span className="text-velcor-green">{post.category}</span>
            <span className="text-slate-300">·</span>
            <span className="text-slate-400 inline-flex items-center gap-1">
              <Clock className="w-3 h-3" /> {post.readTime} min
            </span>
          </div>
          <h1
            className="display text-velcor-ink leading-[1.05]"
            style={{ fontSize: "clamp(30px, 4.4vw, 48px)", fontWeight: 600, letterSpacing: "-0.022em" }}
          >
            {post.title}
          </h1>
          <p className="mt-5 text-slate-500 text-[16px] md:text-[17px] leading-relaxed">{post.excerpt}</p>
          <div className="mt-7 flex flex-wrap gap-x-6 gap-y-2 text-[12.5px] text-slate-400">
            <span className="inline-flex items-center gap-1.5">
              <User className="w-3.5 h-3.5" /> {post.author}
            </span>
            <span className="inline-flex items-center gap-1.5">
              <Calendar className="w-3.5 h-3.5" /> {formatDate(post.publishedAt)}
            </span>
            {altPost && (
              <Link
                to={`/blog/${altPost.slug}`}
                data-testid="blog-lang-switch"
                className="inline-flex items-center gap-1.5 text-velcor-green font-bold hover:underline"
              >
                {isEs ? "Read in English →" : "Leer en español →"}
              </Link>
            )}
          </div>
        </header>

        {/* Cover */}
        <div
          className="aspect-[16/9] rounded-3xl bg-cover bg-center mb-10 border border-slate-200"
          style={{ backgroundImage: `url(${post.cover})` }}
        />

        {/* Body */}
        <div className="text-slate-700" data-testid="blog-post-body">
          <ReactMarkdown
            remarkPlugins={[remarkGfm]}
            components={{
              h2: ({ node, ...props }) => (
                <h2 className="text-velcor-ink font-bold tracking-tight mt-12 mb-4" style={{ fontSize: 24 }} {...props} />
              ),
              h3: ({ node, ...props }) => (
                <h3 className="text-velcor-ink font-bold tracking-tight mt-8 mb-3" style={{ fontSize: 19 }} {...props} />
              ),
              p: ({ node, ...props }) => (
                <p className="my-4 leading-[1.75]" style={{ fontSize: 15.5 }} {...props} />
              ),
              ul: ({ node, ...props }) => (
                <ul className="list-disc pl-6 my-4 space-y-2" style={{ fontSize: 15.5 }} {...props} />
              ),
              ol: ({ node, ...props }) => (
                <ol className="list-decimal pl-6 my-4 space-y-2" style={{ fontSize: 15.5 }} {...props} />
              ),
              li: ({ node, ...props }) => <li className="leading-[1.75]" {...props} />,
              strong: ({ node, ...props }) => <strong className="text-velcor-ink font-bold" {...props} />,
              a: ({ node, ...props }) => (
                <a className="text-velcor-green underline-offset-2 hover:underline" {...props} />
              ),
              blockquote: ({ node, ...props }) => (
                <blockquote className="border-l-4 border-velcor-green bg-velcor-green/5 py-3 px-5 rounded-r-xl my-6 text-slate-700" {...props} />
              ),
              table: ({ node, ...props }) => (
                <div className="overflow-x-auto my-6">
                  <table className="w-full border-collapse text-[14px]" {...props} />
                </div>
              ),
              th: ({ node, ...props }) => (
                <th className="bg-slate-100 text-velcor-ink p-3 text-left font-bold border border-slate-200" {...props} />
              ),
              td: ({ node, ...props }) => (
                <td className="p-3 border border-slate-100 text-slate-600" {...props} />
              ),
              hr: () => <hr className="my-10 border-slate-200" />,
            }}
          >
            {post.body}
          </ReactMarkdown>
        </div>

        {/* Tags */}
        {Array.isArray(post.tags) && post.tags.length > 0 && (
          <div className="mt-12 flex flex-wrap gap-2">
            {post.tags.map((t) => (
              <span key={t} className="px-3 py-1 rounded-full bg-slate-100 text-slate-500 text-[11.5px] font-medium">
                #{t}
              </span>
            ))}
          </div>
        )}
      </article>

      {/* Related */}
      {related.length > 0 && (
        <section className="bg-[#f6f9f5] py-14 md:py-20">
          <div className="max-w-[1180px] mx-auto px-5">
            <div className="text-[11px] font-extrabold uppercase tracking-[0.22em] text-velcor-green mb-3">
              {isEs ? "Artículos relacionados" : "Related articles"}
            </div>
            <h2 className="display text-velcor-ink leading-tight mb-9" style={{ fontSize: "clamp(22px, 3vw, 32px)", fontWeight: 600 }}>
              {isEs ? "Sigue leyendo" : "Keep reading"}
            </h2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {related.map((p) => (
                <Link
                  key={p.slug}
                  to={`/blog/${p.slug}`}
                  className="group block bg-white rounded-3xl border border-slate-200 overflow-hidden hover:border-velcor-green transition-colors"
                >
                  <div className="aspect-[16/10] bg-cover bg-center" style={{ backgroundImage: `url(${p.cover})` }} />
                  <div className="p-5">
                    <div className="text-[10.5px] font-extrabold uppercase tracking-[0.16em] text-velcor-green">{p.category}</div>
                    <h3 className="mt-2 text-velcor-ink font-bold text-[15px] leading-tight group-hover:text-velcor-green transition-colors">
                      {p.title}
                    </h3>
                    <div className="mt-3 inline-flex items-center gap-1 text-velcor-green font-bold text-[12px]">
                      {isEs ? "Leer" : "Read"} <ChevronRight className="w-3.5 h-3.5" />
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}
    </Layout>
  );
}
